import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { ApiGarGarsListRequest, GAR } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGarSingleQuery = (uuid: string) => ({
  queryKey: ["gar-single", uuid],
  queryFn: () => uploadApi.apiGarGarsRead({ uuid }),
});

export const useGarSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<GAR>(queryClient, ["gar-list"], uuid, getGarSingleQuery(uuid));
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGarListQuery = (options: ApiGarGarsListRequest) => ({
  queryKey: ["gar-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGarGarsList(options),
  placeholderData: keepPreviousData,
});

export const useGarListQuery = (options: ApiGarGarsListRequest) => {
  return useQuery(getGarListQuery(options));
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGarQueries = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: ["gar-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGarListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
  }, [queryClient]);
};
